// 図形上で右クリックしたときに表示するコンテキストメニュー。
//
// メニュー項目:
// - ロック / ロック解除（locked プロパティをトグル）
// - 最前面へ移動 (zOrder = 既存 max + 1)
// - 最背面へ移動 (zOrder = 既存 min - 1)
// - 削除（ロック中はメニューから「ロック解除」のみ可能）
//
// 表示位置はクライアント座標。画面端からはみ出す場合は自動でずらす。

import {
  DeleteIconData,
  FlipToBackIconData,
  FlipToFrontIconData,
  FormatAlignCenterIconData,
  FormatAlignLeftIconData,
  FormatAlignRightIconData,
  FormatSizeIconData,
  LockIconData,
  LockOpenIconData,
  PaletteIconData,
} from "@meiden-connect/material-symbols";
import { Icon } from "@meiden-connect/ui";
import type { TextAlign } from "@refinement-canvas/domain";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
} from "react";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { useCanvasStore } from "../store/canvas-store";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";
import {
  clampTextFontSize,
  TEXT_SIZE_MAX_PX,
  TEXT_SIZE_MIN_PX,
} from "../../../lib/text-size-preference";
import { isValidHexColor, normalizeHexColor } from "../../../lib/pen-preference";
import {
  addShapeCustomColor,
  loadShapeCustomColors,
  SHAPE_FILL_NONE,
  SHAPE_FILL_PRESET_COLORS,
} from "../../../lib/shape-preference";

// 図形の背景を「なし」にするときに送る fill 値。op スキーマは「未設定」を表現できないため、
// 透明色を明示的に設定して塗りなし表示にする（描画は fill="transparent" 相当）。
const SHAPE_FILL_TRANSPARENT = "transparent";

const MENU_WIDTH_PX = 200;
const MENU_MARGIN_PX = 8;

export function ObjectContextMenu(): JSX.Element | null {
  const contextMenu = useCanvasStore((s) => s.contextMenu);
  const closeContextMenu = useCanvasStore((s) => s.closeContextMenu);
  const objects = useCanvasDocStore((s) => s.objects);
  const dispatcher = useCanvasOpDispatcherContext();
  const menuRef = useRef<HTMLUListElement | null>(null);
  // 色変更メニューをクリックしたときに開くネイティブカラーピッカーへの参照。
  const colorInputRef = useRef<HTMLInputElement | null>(null);
  // 図形の背景色カスタム追加で開くネイティブカラーピッカーへの参照。
  const fillInputRef = useRef<HTMLInputElement | null>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  // 「サイズ変更」を選んだあとの px 入力 UI 表示状態。
  const [sizeInputOpen, setSizeInputOpen] = useState(false);
  const [sizeDraft, setSizeDraft] = useState("16");
  // 「文字寄せ」を選んだあとの 左/中央/右 選択 UI 表示状態。
  const [alignOpen, setAlignOpen] = useState(false);
  // 「背景色」を選んだあとの色見本選択 UI 表示状態と、図形背景色のカスタム色一覧。
  const [fillOpen, setFillOpen] = useState(false);
  const [shapeCustomColors, setShapeCustomColors] = useState<readonly string[]>([]);

  // contextMenu が開く度に、サイズ入力・文字寄せ選択モードを初期状態へ戻し、対象テキストの現サイズを下書きにセットする。
  useEffect(() => {
    setSizeInputOpen(false);
    setAlignOpen(false);
    setFillOpen(false);
    if (!contextMenu) {
      return;
    }
    const target = useCanvasDocStore.getState().objects[contextMenu.objectId];
    if (target?.type === "text") {
      setSizeDraft(String(target.fontSize ?? 16));
    }
    if (target?.type === "shape") {
      // 背景色の色見本に最新のカスタム色を反映する（他タブ追加やツールバー追加に追従）。
      setShapeCustomColors(loadShapeCustomColors());
    }
  }, [contextMenu]);

  // メニュー外クリック / Escape / スクロールで閉じる。
  useEffect(() => {
    if (!contextMenu) return undefined;
    const onDown = (event: MouseEvent | PointerEvent): void => {
      if (menuRef.current && menuRef.current.contains(event.target as Node)) return;
      closeContextMenu();
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") closeContextMenu();
    };
    const onScroll = (): void => closeContextMenu();
    window.addEventListener("pointerdown", onDown, true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("wheel", onScroll, { passive: true });
    window.addEventListener("resize", closeContextMenu);
    return () => {
      window.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("wheel", onScroll);
      window.removeEventListener("resize", closeContextMenu);
    };
  }, [contextMenu, closeContextMenu]);

  // 画面端で右や下へはみ出す場合は左 / 上へ寄せる。
  useLayoutEffect(() => {
    if (!contextMenu) {
      setPosition(null);
      return;
    }
    const menuEl = menuRef.current;
    const menuWidth = menuEl?.offsetWidth ?? MENU_WIDTH_PX;
    const menuHeight = menuEl?.offsetHeight ?? 200;
    const maxLeft = window.innerWidth - menuWidth - MENU_MARGIN_PX;
    const maxTop = window.innerHeight - menuHeight - MENU_MARGIN_PX;
    setPosition({
      left: Math.max(MENU_MARGIN_PX, Math.min(contextMenu.clientX, maxLeft)),
      top: Math.max(MENU_MARGIN_PX, Math.min(contextMenu.clientY, maxTop)),
    });
  }, [contextMenu]);

  if (!contextMenu) return null;
  const target = objects[contextMenu.objectId];
  if (!target) return null;

  const isLocked = target.locked === true;

  const handleToggleLock = (): void => {
    dispatcher.transformObject(target.id, { locked: !isLocked });
    closeContextMenu();
  };

  const handleBringToFront = (): void => {
    if (isLocked) {
      closeContextMenu();
      return;
    }
    const all = Object.values(objects);
    const maxZ = all.length > 0 ? Math.max(...all.map((o) => o.zOrder)) : 0;
    dispatcher.transformObject(target.id, { zOrder: maxZ + 1 });
    closeContextMenu();
  };

  const handleSendToBack = (): void => {
    if (isLocked) {
      closeContextMenu();
      return;
    }
    const all = Object.values(objects);
    const minZ = all.length > 0 ? Math.min(...all.map((o) => o.zOrder)) : 0;
    dispatcher.transformObject(target.id, { zOrder: minZ - 1 });
    closeContextMenu();
  };

  const handleDelete = (): void => {
    if (isLocked) {
      closeContextMenu();
      return;
    }
    dispatcher.deleteObjects([target.id]);
    useCanvasStore.getState().setSelectedObjectIds([]);
    closeContextMenu();
  };

  // 色変更が可能なオブジェクト種別か（sticky / pen-stroke）。テキストは色変更よりサイズ変更を優先する。
  const canChangeColor = target.type === "sticky" || target.type === "pen-stroke";
  // 背景色（fill）変更が可能なオブジェクト種別か。
  // 矢印は塗りつぶせる領域を持たない（線＋矢じり）ため、丸 / 四角のみ対象にする。
  const canChangeShapeFill = target.type === "shape" && target.shapeKind !== "arrow";
  // 図形の現在の背景色。未設定・transparent は「背景なし」扱い。
  const currentShapeFill =
    target.type === "shape" ? target.fill : undefined;
  const isShapeFillNone =
    currentShapeFill === undefined || currentShapeFill === SHAPE_FILL_TRANSPARENT;
  // サイズ変更が可能なオブジェクト種別か（テキストのみ）。fontSize を直接変える。
  const canChangeFontSize = target.type === "text";
  // 文字寄せ変更が可能なオブジェクト種別か（テキスト / 付箋）。textAlign を直接変える。
  const canChangeTextAlign = target.type === "text" || target.type === "sticky";

  const handleChangeColor = (): void => {
    if (isLocked) {
      closeContextMenu();
      return;
    }
    // 隠した <input type="color"> を開くため、メニュー本体は閉じない。
    // 利用者がピッカーで色を確定するか、メニュー外をクリックするまでメニューを保持する。
    colorInputRef.current?.click();
  };

  const handleColorPicked = (event: ChangeEvent<HTMLInputElement>): void => {
    const color = event.currentTarget.value;
    dispatcher.updateObjectStyle(target.id, { color });
    closeContextMenu();
  };

  const handleChangeFontSize = (): void => {
    if (isLocked) {
      closeContextMenu();
      return;
    }
    setSizeInputOpen(true);
  };

  const handleCommitFontSize = (): void => {
    const px = clampTextFontSize(Number.parseInt(sizeDraft, 10));
    dispatcher.updateObjectStyle(target.id, { fontSize: px });
    closeContextMenu();
  };

  const handleChangeTextAlign = (): void => {
    if (isLocked) {
      closeContextMenu();
      return;
    }
    setAlignOpen(true);
  };

  const handleChangeShapeFill = (): void => {
    if (isLocked) {
      closeContextMenu();
      return;
    }
    setFillOpen(true);
  };

  // 背景色の色見本を選んだとき。"none" は透明色を、それ以外は hex を fill として送る。
  const handlePickShapeFill = (fill: string): void => {
    dispatcher.updateObjectStyle(target.id, { fill });
    closeContextMenu();
  };

  // 背景色の「＋」でネイティブカラーピッカーを開く。メニューは閉じない。
  const handleAddShapeFill = (): void => {
    fillInputRef.current?.click();
  };

  const handleShapeFillPicked = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.currentTarget.value;
    if (isValidHexColor(value)) {
      const normalized = normalizeHexColor(value);
      setShapeCustomColors(addShapeCustomColor(normalized));
      dispatcher.updateObjectStyle(target.id, { fill: normalized });
    }
    closeContextMenu();
  };

  const handlePickTextAlign = (align: TextAlign): void => {
    dispatcher.updateObjectStyle(target.id, { textAlign: align });
    closeContextMenu();
  };

  // 現在の文字寄せ（未指定は左寄せ扱い）。選択状態の強調表示に使う。
  const currentTextAlign: TextAlign =
    (target.type === "text" || target.type === "sticky" ? target.textAlign : undefined) ?? "left";

  return (
    <ul
      ref={menuRef}
      role="menu"
      aria-label="図形のコンテキストメニュー"
      data-testid="object-context-menu"
      style={{
        position: "fixed",
        left: position?.left ?? contextMenu.clientX,
        top: position?.top ?? contextMenu.clientY,
        margin: 0,
        padding: "4px",
        listStyle: "none",
        minWidth: `${MENU_WIDTH_PX}px`,
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-2)",
        boxShadow: "var(--shadow-2)",
        zIndex: 1000,
        fontSize: "13px",
        color: "var(--color-text)",
      }}
    >
      <MenuItem
        icon={isLocked ? LockOpenIconData : LockIconData}
        label={isLocked ? "ロック解除" : "ロック"}
        onClick={handleToggleLock}
      />
      <MenuItem
        icon={FlipToFrontIconData}
        label="最前面へ移動"
        onClick={handleBringToFront}
        disabled={isLocked}
      />
      <MenuItem
        icon={FlipToBackIconData}
        label="最背面へ移動"
        onClick={handleSendToBack}
        disabled={isLocked}
      />
      {canChangeColor ? (
        <MenuItem
          icon={PaletteIconData}
          label="色変更"
          onClick={handleChangeColor}
          disabled={isLocked}
        />
      ) : null}
      {canChangeShapeFill ? (
        fillOpen ? (
          <li role="none" style={{ margin: 0 }}>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "6px",
                padding: "8px 12px",
                maxWidth: "220px",
              }}
            >
              <FillSwatch
                label="背景なし"
                none
                selected={isShapeFillNone}
                onClick={() => handlePickShapeFill(SHAPE_FILL_TRANSPARENT)}
              />
              {SHAPE_FILL_PRESET_COLORS.map((color) => (
                <FillSwatch
                  key={`preset-${color}`}
                  label={color}
                  color={color}
                  selected={
                    !isShapeFillNone &&
                    currentShapeFill !== undefined &&
                    normalizeHexColor(currentShapeFill) === normalizeHexColor(color)
                  }
                  onClick={() => handlePickShapeFill(color)}
                />
              ))}
              {shapeCustomColors
                .filter((c) => !SHAPE_FILL_PRESET_COLORS.includes(c))
                .map((color) => (
                  <FillSwatch
                    key={`custom-${color}`}
                    label={color}
                    color={color}
                    selected={
                      !isShapeFillNone &&
                      currentShapeFill !== undefined &&
                      normalizeHexColor(currentShapeFill) === normalizeHexColor(color)
                    }
                    onClick={() => handlePickShapeFill(color)}
                  />
                ))}
              <FillSwatch label="カスタム色を追加" add onClick={handleAddShapeFill} />
            </div>
          </li>
        ) : (
          <MenuItem
            icon={PaletteIconData}
            label="背景色"
            onClick={handleChangeShapeFill}
            disabled={isLocked}
          />
        )
      ) : null}
      {canChangeFontSize ? (
        sizeInputOpen ? (
          <li role="none" style={{ margin: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 12px",
              }}
            >
              <Icon d={FormatSizeIconData} />
              <input
                type="number"
                inputMode="numeric"
                min={TEXT_SIZE_MIN_PX}
                max={TEXT_SIZE_MAX_PX}
                step={1}
                value={sizeDraft}
                aria-label="文字サイズ（px 指定）"
                onChange={(event) => setSizeDraft(event.currentTarget.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") handleCommitFontSize();
                  if (event.key === "Escape") closeContextMenu();
                }}
                style={{
                  width: "72px",
                  padding: "4px 6px",
                  border: "1px solid var(--color-border, #b6b6c6)",
                  borderRadius: "var(--radius-1, 4px)",
                  font: "inherit",
                  color: "inherit",
                }}
                autoFocus
              />
              <span style={{ fontSize: "12px", color: "var(--color-text-muted, #6b6f7d)" }}>px</span>
              <button
                type="button"
                onClick={handleCommitFontSize}
                style={{
                  padding: "4px 8px",
                  border: "1px solid var(--color-border, #b6b6c6)",
                  borderRadius: "var(--radius-1, 4px)",
                  background: "var(--color-surface, #fff)",
                  cursor: "pointer",
                  font: "inherit",
                }}
              >
                適用
              </button>
            </div>
          </li>
        ) : (
          <MenuItem
            icon={FormatSizeIconData}
            label="サイズ変更"
            onClick={handleChangeFontSize}
            disabled={isLocked}
          />
        )
      ) : null}
      {canChangeTextAlign ? (
        alignOpen ? (
          <li role="none" style={{ margin: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 12px",
              }}
            >
              {TEXT_ALIGN_MENU_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={currentTextAlign === option.value}
                  aria-label={option.label}
                  title={option.label}
                  // 展開直後にキーボード操作を継続できるよう、現在の寄せに対応するボタンへフォーカスする。
                  autoFocus={currentTextAlign === option.value}
                  onClick={() => handlePickTextAlign(option.value)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "4px 8px",
                    border: "1px solid var(--color-border, #b6b6c6)",
                    borderRadius: "var(--radius-1, 4px)",
                    background:
                      currentTextAlign === option.value
                        ? "var(--color-surface-muted, #eef)"
                        : "var(--color-surface, #fff)",
                    cursor: "pointer",
                    color: "inherit",
                    font: "inherit",
                  }}
                >
                  <Icon d={option.icon} />
                </button>
              ))}
            </div>
          </li>
        ) : (
          <MenuItem
            icon={textAlignMenuIcon(currentTextAlign)}
            label="文字寄せ"
            onClick={handleChangeTextAlign}
            disabled={isLocked}
          />
        )
      ) : null}
      <MenuItem
        icon={DeleteIconData}
        label="削除"
        onClick={handleDelete}
        disabled={isLocked}
        danger
      />
      {/* 「色変更」をクリックした際にネイティブのカラーピッカーを開くための、視覚的に隠した input。 */}
      <li role="none" style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}>
        <input
          ref={colorInputRef}
          type="color"
          onChange={handleColorPicked}
          aria-hidden="true"
          tabIndex={-1}
          style={{ width: 0, height: 0, opacity: 0, border: "none", padding: 0 }}
        />
      </li>
      {/* 図形の背景色「＋」でネイティブカラーピッカーを開くための、視覚的に隠した input。 */}
      <li role="none" style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}>
        <input
          ref={fillInputRef}
          type="color"
          onChange={handleShapeFillPicked}
          aria-hidden="true"
          tabIndex={-1}
          style={{ width: 0, height: 0, opacity: 0, border: "none", padding: 0 }}
        />
      </li>
    </ul>
  );
}

// 図形の背景色メニューで使う色見本ボタン。
// - none: 背景なし（白地に赤い斜線）
// - add: カスタム色追加（破線円に＋）
// - それ以外: color を塗った丸
function FillSwatch({
  label,
  color,
  selected,
  none,
  add,
  onClick,
}: {
  readonly label: string;
  readonly color?: string | undefined;
  readonly selected?: boolean | undefined;
  readonly none?: boolean | undefined;
  readonly add?: boolean | undefined;
  readonly onClick: () => void;
}): JSX.Element {
  const base: CSSProperties = {
    width: "22px",
    height: "22px",
    borderRadius: "50%",
    cursor: "pointer",
    padding: 0,
    boxShadow: selected ? "0 0 0 3px var(--color-accent, #1e63d4)" : undefined,
    outline: "none",
  };
  const style: CSSProperties = add
    ? {
        ...base,
        border: "1.5px dashed var(--color-border, #b6b6c6)",
        display: "grid",
        placeItems: "center",
        color: "var(--color-text-muted, #6b6f7d)",
        background: "transparent",
        fontSize: "14px",
        lineHeight: 1,
      }
    : none
      ? {
          ...base,
          border: "1px solid rgba(0,0,0,0.18)",
          background:
            "linear-gradient(to top right, transparent calc(50% - 1px), #d22b2b calc(50% - 1px), #d22b2b calc(50% + 1px), transparent calc(50% + 1px)), #fff",
        }
      : {
          ...base,
          border: "1px solid rgba(0,0,0,0.08)",
          background: color,
        };
  return (
    <button
      type="button"
      role={add ? "menuitem" : "menuitemradio"}
      aria-checked={add ? undefined : selected}
      aria-label={label}
      title={label}
      onClick={onClick}
      style={style}
    >
      {add ? "＋" : null}
    </button>
  );
}

// 文字寄せメニューの選択肢（左/中央/右）。
const TEXT_ALIGN_MENU_OPTIONS: readonly {
  readonly value: TextAlign;
  readonly label: string;
  readonly icon: string;
}[] = [
  { value: "left", label: "左寄せ", icon: FormatAlignLeftIconData },
  { value: "center", label: "中央寄せ", icon: FormatAlignCenterIconData },
  { value: "right", label: "右寄せ", icon: FormatAlignRightIconData },
];

// 現在の文字寄せに対応するアイコン（メニュー項目の見出し用）。
function textAlignMenuIcon(align: TextAlign): string {
  switch (align) {
    case "center":
      return FormatAlignCenterIconData;
    case "right":
      return FormatAlignRightIconData;
    default:
      return FormatAlignLeftIconData;
  }
}

function MenuItem({
  icon,
  label,
  onClick,
  disabled,
  danger,
}: {
  readonly icon: string;
  readonly label: string;
  readonly onClick: () => void;
  readonly disabled?: boolean | undefined;
  readonly danger?: boolean | undefined;
}): JSX.Element {
  return (
    <li role="none" style={{ margin: 0 }}>
      <button
        role="menuitem"
        type="button"
        disabled={disabled}
        onClick={onClick}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          width: "100%",
          padding: "8px 12px",
          background: "transparent",
          border: "none",
          borderRadius: "var(--radius-1)",
          textAlign: "left",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          color: danger && !disabled ? "#d22b2b" : "inherit",
          font: "inherit",
        }}
        onMouseEnter={(event) => {
          if (!disabled) event.currentTarget.style.background = "var(--color-surface-muted)";
        }}
        onMouseLeave={(event) => {
          event.currentTarget.style.background = "transparent";
        }}
      >
        <Icon d={icon} />
        <span>{label}</span>
      </button>
    </li>
  );
}
